// Frontend client for the analyse pipeline.
//
// Backend runs as a Netlify Background Function with three sequential passes;
// the frontend uses a job-id + poll pattern with progressive partials:
//
//   1. POST /.netlify/functions/start-analysis with { text, documentType }
//      → 202 { jobId }
//   2. GET /.netlify/functions/status?jobId=… every ~2.5s. Each response may
//      include a `partialResult` (after Pass 1 / Pass 2) or `result` (when
//      stage === 'done'). The InputScreen navigates as soon as the first
//      partial lands; the ResultScreen continues polling.
//   3. Permalink reads use GET /.netlify/functions/result?slug=… which serves
//      from the slug-keyed Blobs store.

import type { AnalysisResult, DocumentType, Sector } from './types';
import { getUserId } from './userId';

/** Headers attached to every API request so the backend can rate-limit
 *  per browser-installed user instead of per IP. */
function authHeaders(extra?: HeadersInit): HeadersInit {
  return { 'X-TIA-User-Id': getUserId(), ...(extra ?? {}) };
}

export type JobStage = 'queued' | 'extract' | 'score' | 'synthesize' | 'done' | 'error';

export interface JobStatus {
  id: string;
  stage: JobStage;
  message?: string;
  createdAt: string;
  updatedAt: string;
  timings?: Partial<Record<Exclude<JobStage, 'queued'>, number>>;
  partialResult?: AnalysisResult;
  result?: AnalysisResult;
  slug?: string;
}

export class AnalyseError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AnalyseError';
    this.status = status;
  }
}

export interface QuotaState {
  used: number;
  limit: number;
  remaining: number;
  monthKey: string;
  resetAt: string;
}

export async function fetchQuota(signal?: AbortSignal): Promise<QuotaState> {
  const resp = await fetch('/.netlify/functions/quota', {
    method: 'GET',
    headers: authHeaders(),
    signal,
  });
  if (!resp.ok) throw new AnalyseError(`Quota fetch failed (${resp.status})`, resp.status);
  return (await resp.json()) as QuotaState;
}

export async function startAnalysis(input: {
  text: string;
  documentType: DocumentType;
  ticker?: string;
  period?: string;
  sector?: Sector;
  signal?: AbortSignal;
}): Promise<{ jobId: string; quota?: QuotaState }> {
  const resp = await fetch('/.netlify/functions/start-analysis', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      text: input.text,
      documentType: input.documentType,
      ticker: input.ticker,
      period: input.period,
      sector: input.sector,
    }),
    signal: input.signal,
  });
  if (!resp.ok) {
    let message = `Failed to start analysis (${resp.status})`;
    let quota: QuotaState | undefined;
    try {
      const data = (await resp.json()) as { error?: string; quota?: QuotaState };
      if (data.error) message = data.error;
      quota = data.quota;
    } catch { /* default message */ }
    const err = new AnalyseError(message, resp.status);
    if (quota) (err as AnalyseError & { quota?: QuotaState }).quota = quota;
    throw err;
  }
  return (await resp.json()) as { jobId: string; quota?: QuotaState };
}

export async function fetchStatus(jobId: string, signal?: AbortSignal): Promise<JobStatus> {
  const resp = await fetch(`/.netlify/functions/status?jobId=${encodeURIComponent(jobId)}`, {
    method: 'GET',
    headers: authHeaders(),
    signal,
  });
  if (resp.status === 404) {
    throw new AnalyseError('Job not found.', 404);
  }
  if (!resp.ok) {
    let message = `Status fetch failed (${resp.status})`;
    try {
      const data = (await resp.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch { /* default */ }
    throw new AnalyseError(message, resp.status);
  }
  return (await resp.json()) as JobStatus;
}

export interface ExtractedTags {
  ticker: string | null;
  period: string | null;
  sector: Sector | null;
  quota?: QuotaState;
}

export async function extractTags(text: string, signal?: AbortSignal): Promise<ExtractedTags> {
  const resp = await fetch('/.netlify/functions/extract-tags', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ text }),
    signal,
  });
  if (!resp.ok) {
    let message = `Auto-detect failed (${resp.status})`;
    let quota: QuotaState | undefined;
    try {
      const data = (await resp.json()) as { error?: string; quota?: QuotaState };
      if (data.error) message = data.error;
      quota = data.quota;
    } catch { /* default */ }
    const err = new AnalyseError(message, resp.status);
    if (quota) (err as AnalyseError & { quota?: QuotaState }).quota = quota;
    throw err;
  }
  return (await resp.json()) as ExtractedTags;
}

/** Compact summary entry returned by the public library listing endpoint. */
export interface LibraryEntry {
  slug: string;
  title: string;
  date: string;
  documentType: DocumentType;
  verdict: import('./types').Verdict;
  score: number;
  confidence: number;
  ticker?: string;
  period?: string;
  sector?: Sector;
  headline?: string;
  /** Opaque hash of the user who ran the analysis. Not the user id itself —
   *  it's only useful for "is this entry mine?" comparisons. */
  ownerHash?: string;
}

export interface LibraryPage {
  total: number;
  offset: number;
  limit: number;
  entries: LibraryEntry[];
}

export interface LibraryQuery {
  ticker?: string;
  sector?: Sector;
  period?: string;
  verdict?: import('./types').Verdict;
  docType?: DocumentType;
  q?: string;
  mineOnly?: boolean;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

export async function listResults(query: LibraryQuery = {}): Promise<LibraryPage> {
  const params = new URLSearchParams();
  if (query.ticker) params.set('ticker', query.ticker);
  if (query.sector) params.set('sector', query.sector);
  if (query.period) params.set('period', query.period);
  if (query.verdict) params.set('verdict', query.verdict);
  if (query.docType) params.set('docType', query.docType);
  if (query.q) params.set('q', query.q);
  if (query.mineOnly) params.set('mine', '1');
  if (query.limit != null) params.set('limit', String(query.limit));
  if (query.offset != null) params.set('offset', String(query.offset));
  const qs = params.toString();
  const resp = await fetch(`/.netlify/functions/list-results${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: authHeaders(),
    signal: query.signal,
  });
  if (!resp.ok) {
    let message = `Library fetch failed (${resp.status})`;
    try {
      const data = (await resp.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch { /* default */ }
    throw new AnalyseError(message, resp.status);
  }
  return (await resp.json()) as LibraryPage;
}

export async function fetchResultBySlug(slug: string, signal?: AbortSignal): Promise<AnalysisResult | null> {
  const resp = await fetch(`/.netlify/functions/result?slug=${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: authHeaders(),
    signal,
  });
  if (resp.status === 404) return null;
  if (!resp.ok) {
    throw new AnalyseError(`Result fetch failed (${resp.status})`, resp.status);
  }
  return (await resp.json()) as AnalysisResult;
}

interface PollOptions {
  jobId: string;
  signal?: AbortSignal;
  /** Called once on each new stage with the latest JobStatus. */
  onProgress?: (status: JobStatus) => void;
  /** Called the first time a partialResult arrives, then for each update. */
  onPartial?: (partial: AnalysisResult, status: JobStatus) => void;
  intervalMs?: number;
  maxDurationMs?: number;
  /** If true, resolve as soon as the first partial result arrives instead of
   *  waiting for stage === 'done'. The caller is responsible for continuing
   *  to poll if it wants subsequent updates. */
  resolveOnFirstPartial?: boolean;
}

export async function pollUntilDone(opts: PollOptions): Promise<AnalysisResult> {
  const {
    jobId, signal, onProgress, onPartial,
    intervalMs = 2500, maxDurationMs = 15 * 60 * 1000,
    resolveOnFirstPartial,
  } = opts;
  const t0 = Date.now();
  let lastStage: JobStage | null = null;
  let lastPartialUpdatedAt: string | null = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) throw new AnalyseError('Analysis cancelled.', 0);
    if (Date.now() - t0 > maxDurationMs) throw new AnalyseError('Analysis exceeded the 15-minute time budget.', 504);

    const status = await fetchStatus(jobId, signal);
    if (status.stage !== lastStage) {
      lastStage = status.stage;
      onProgress?.(status);
    }
    if (status.partialResult && status.updatedAt !== lastPartialUpdatedAt) {
      lastPartialUpdatedAt = status.updatedAt;
      onPartial?.(status.partialResult, status);
      if (resolveOnFirstPartial) return status.partialResult;
    }
    if (status.stage === 'done') {
      if (!status.result) throw new AnalyseError('Job completed without a result.', 500);
      return status.result;
    }
    if (status.stage === 'error') {
      throw new AnalyseError(status.message ?? 'Analysis failed.', 500);
    }

    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, intervalMs);
      signal?.addEventListener('abort', () => { clearTimeout(t); reject(new AnalyseError('Analysis cancelled.', 0)); }, { once: true });
    });
  }
}
